import React, { useState, useEffect } from 'react';
import { Button, Typography, Space, Row, Col, Card, Statistic, Avatar, Steps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  MessageOutlined, 
  RobotOutlined, 
  SendOutlined,
  BulbOutlined,
  StarOutlined,
  SafetyOutlined,
  UserOutlined,
  LoginOutlined,
  UserAddOutlined,
  CrownOutlined,
  ArrowRightOutlined,
  ThunderboltOutlined,
  SearchOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  SmileOutlined,
  HeartOutlined,
  SecurityScanOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { chatGPTTheme } from '../../assets/styles/theme';

const { Title, Paragraph, Text } = Typography;

// 主容器 - 参考 AdoAI_homepage.html 的白色背景
const HomeContainer = styled.div`
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  background: #ffffff;
  font-family: 'Hiragino Kaku Gothic ProN', 'Hiragino Sans', 'Meiryo', sans-serif;
  line-height: 1.6;
  color: #333;
  overflow-x: hidden;  // 防止水平滚动
`;

// 英雄区域 - 参考 AdoAI_homepage.html 的渐变背景
const HeroSection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 64px 0 64px;
  text-align: center;
  color: white;
  position: relative;
  overflow: hidden;
  width: 100%;
  min-width: 100%;
  margin: 0;
  
  @media (max-width: 768px) {
    padding: 12px 0 40px;  // 调整上内边距为12px，保持合理间距
    width: 100vw;
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle 300px at 20% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle 200px at 80% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

// 媒体报道区域 - 参考 AdoAI_homepage.html
const FeaturedSection = styled.div`
  background: #f8f9fa;
  padding: 3rem 0;
  text-align: center;
  border-bottom: 1px solid #e5e7eb;
`;

const FeaturedLogos = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3rem;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const FeaturedLogo = styled.div`
  padding: 10px 20px;
  background: #f0f0f0;
  border-radius: 5px;
  font-weight: bold;
  color: #666;
  opacity: 0.6;
  transition: opacity 0.3s;

  &:hover {
    opacity: 1;
  }
`;

// 服务特色区域
const ServicesSection = styled.div`
  background: #ffffff;
  padding: 80px 0;
  
  @media (max-width: 768px) {
    padding: 40px 16px;  // 移动端减小上下内边距，添加左右内边距
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
`;

// 服务特色标题区域
const ServicesTitleSection = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  .title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #333;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
    
    .title {
      font-size: 1.8rem;
      margin-bottom: 0.6rem;
    }
    
    .description {
      font-size: 0.85rem;
      line-height: 1.3;
      max-width: 100%;
      padding: 0 12px;
      color: #666;
    }
  }
`;

// 三步骤说明区域 - 参考 AdoAI_homepage.html
const HowItWorksSection = styled.div`
  background: #f8f9fa;
  padding: 80px 0;
  
  @media (max-width: 768px) {
    padding: 40px 16px;  // 减小上下内边距，添加左右内边距
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
`;

const StepContainer = styled.div`
  text-align: center;
  padding: 2rem;
  flex: 1;
  min-width: 250px;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;  // 减小内边距
    min-width: auto;  // 移除最小宽度限制
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 auto !important;
    box-sizing: border-box !important;
  }
`;

const StepNumber = styled.div`
  background: #667eea;
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1rem;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  
  @media (max-width: 768px) {
    width: 48px;  // 减小圆圈大小
    height: 48px;
    font-size: 1.2rem;
    margin: 0 auto 0.5rem;  // 减小底部间距
  }
`;

// 用户证言区域 - 参考 AdoAI_homepage.html
const TestimonialsSection = styled.div`
  background: #ffffff;
  padding: 80px 0;
  
  @media (max-width: 768px) {
    padding: 40px 16px;  // 减小上下内边距，添加左右内边距
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
`;

const TestimonialCard = styled(Card)`
  &.ant-card {
    border-radius: 15px;
    border-left: 4px solid #667eea;
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    height: 100%;
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
    }

    .ant-card-body {
      padding: 32px;
      
      @media (max-width: 768px) {
        padding: 20px 16px;  // 调整移动端内边距
      }
    }
    
    // 移动端样式调整
    @media (max-width: 768px) {
      max-width: 100% !important;
      width: 100% !important;
      margin: 0 auto !important;
      box-sizing: border-box !important;
      overflow: visible !important;
    }
  }
`;

// CTA区域 - 参考 AdoAI_homepage.html
const CTASection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 80px 0;
  text-align: center;
  color: white;
  
  @media (max-width: 768px) {
    padding: 40px 16px;  // 减小上下内边距，添加左右内边距
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
`;

// 功能卡片 - 更新样式
const ServiceCard = styled(Card)`
  &.ant-card {
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
    height: 100%;
    text-align: center;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 20px 40px rgba(0,0,0,0.15);
    }

    .ant-card-body {
      padding: 32px 24px;
      
      @media (max-width: 768px) {
        padding: 20px 16px;  // 调整移动端内边距
      }
    }
    
    // 移动端样式调整
    @media (max-width: 768px) {
      max-width: 100% !important;
      width: 100% !important;
      margin: 0 auto !important;
      box-sizing: border-box !important;
      
      .ant-typography {  // 卡片标题
        &.ant-typography-h3 {
          font-size: 1.1rem !important;  // 从0.9rem调整到1.1rem
          margin-bottom: 0.5rem !important;  // 增加一点间距
          font-weight: 600 !important;
        }
      }
      
      .ant-typography-paragraph {  // 卡片描述
        font-size: 0.9rem !important;  // 从0.75rem调整到0.9rem
        line-height: 1.4;  // 调整行高
        margin-bottom: 0 !important;
        color: #666;
      }
      
      .anticon {  // 图标
        font-size: 20px;  // 调整回原来的大小
        margin-bottom: 8px;  // 调整回原来的间距
      }
    }
  }
`;

// 现代化按钮样式
const ModernButton = styled(Button)`
  &.ant-btn {
    height: 48px;
    border-radius: 25px;
    font-size: 16px;
    font-weight: 600;
    border: none;
    transition: all 0.3s ease;
    min-width: 160px;

    @media (max-width: 768px) {
      height: 44px;
      font-size: 14px;
      min-width: 140px;
      border-radius: 22px;
    }

    &.primary {
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: white;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
      }
    }

    &.secondary {
      background: white;
      color: #667eea;
      border: 2px solid #667eea;

      &:hover {
        background: #667eea;
        color: white;
        transform: translateY(-2px);
      }
    }

    &.white {
      background: rgba(255, 255, 255, 0.9);
      color: #667eea;
      border: none;

      &:hover {
        background: white;
        transform: translateY(-2px);
      }
    }

    &.transparent {
      background: transparent;
      color: white;
      border: 2px solid white;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        transform: translateY(-2px);
      }
    }
  }
`;

// 统计数据区域
const StatsSection = styled.div`
  background: #ffffff;
  padding: 60px 0;
  border-bottom: 1px solid #e2e8f0;
`;

// 英雄区域标题
const HeroTitle = styled(Typography.Title)`
  &.ant-typography {
    font-size: 3.5rem;
    font-weight: 700;
    color: white;
    margin-bottom: 0;
    line-height: 1.2;
    
    @media (max-width: 768px) {
      font-size: 2rem !important;  // 调整为2rem，保持更好的可读性
      line-height: 1.2;
      margin-bottom: 8px;  // 增加一点间距
    }
  }
`;

// 英雄区域副标题
const HeroSubtitle = styled.div`
  font-size: 1.6rem;
  font-weight: 350;
  color: white;
  margin-bottom: 1.2rem;  // 减小间距
  white-space: pre-line;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;  // 调整为1.1rem，保持可读性
    margin-bottom: 1rem;  // 增加间距
    line-height: 1.4;  // 增加行高提升可读性
  }
`;

// 英雄区域标语
const HeroSlogan = styled(Typography.Paragraph)`
  &.ant-typography {
    font-size: 1.3rem;
    font-weight: 550;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.4;
    margin-bottom: 1.5rem;  // 减小间距
    max-width: 600px;
    margin: 0 auto 1.5rem;
    white-space: pre-line;
    
    @media (max-width: 768px) {
      font-size: 1rem;    // 调整为1rem，提升可读性
      line-height: 1.5;   // 增加行高
      margin-bottom: 1.5rem; // 增加间距
      max-width: 100%;    // 移动端占满宽度
      padding: 0 16px;    // 添加左右内边距
    }
  }
`;

// 响应式列容器
const ResponsiveCol = styled(Col)`
  padding: 0;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0;
    margin: 0;
    max-width: 100%;
  }
`;

const StyledTitle = styled(Title)`
  &.ant-typography {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 1.8rem !important;
    }
  }
`;

const TestimonialSection = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  .title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #333;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
    
    .title {
      font-size: 1.8rem;
      margin-bottom: 0.6rem;
    }
  }
`;

const CTATitle = styled(Title)`
  &.ant-typography {
    font-size: 2.5rem;
    font-weight: 700;
    color: white;
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 1.8rem !important;
    }
  }
`;

const getTitleStyle = (isMobile: boolean, isWhite: boolean = false) => ({
  fontSize: isMobile ? '1.8rem' : '2.5rem',  // 移动端调整回1.8rem保持可读性
  fontWeight: 700,
  color: isWhite ? 'white' : '#333',
  marginBottom: isMobile ? '0.8rem' : '1rem'  // 移动端减小底部间距
});

// 免责声明区域样式
const DisclaimerSection = styled.div`
  background: #f8f9fa;
  padding: 4rem 0;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const DisclaimerCard = styled(Card)`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 0 1rem;
  }
  
  .ant-card-body {
    padding: 0;
  }
`;

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { state } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const features = [
    {
      icon: <BulbOutlined style={{ fontSize: '3rem', color: '#667eea' }} />,
      title: t('homepage.features.aiAnalysis.title'),
      description: t('homepage.features.aiAnalysis.description')
    },
    {
      icon: <HeartOutlined style={{ fontSize: '3rem', color: '#667eea' }} />,
      title: t('homepage.features.fastSearch.title'),
      description: t('homepage.features.fastSearch.description')
    },
    {
      icon: <SafetyOutlined style={{ fontSize: '3rem', color: '#667eea' }} />,
      title: t('homepage.features.professional.title'),
      description: t('homepage.features.professional.description')
    },
    {
      icon: <FileTextOutlined style={{ fontSize: '3rem', color: '#667eea' }} />,
      title: t('homepage.features.instantResponse.title'),
      description: t('homepage.features.instantResponse.description')
    },
    {
      icon: <ThunderboltOutlined style={{ fontSize: '3rem', color: '#667eea' }} />,
      title: t('homepage.features.detailedReport.title'),
      description: t('homepage.features.detailedReport.description')
    },
    {
      icon: <SecurityScanOutlined style={{ fontSize: '3rem', color: '#667eea' }} />,
      title: t('homepage.features.personalized.title'),
      description: t('homepage.features.personalized.description')
    }
  ];

  const steps = [
    {
      number: 1,
      title: t('homepage.steps.step1.title'),
      description: t('homepage.steps.step1.description')
    },
    {
      number: 2,
      title: t('homepage.steps.step2.title'),
      description: t('homepage.steps.step2.description')
    },
    {
      number: 3,
      title: t('homepage.steps.step3.title'),
      description: t('homepage.steps.step3.description')
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: t('homepage.testimonials.customer1.name'),
      title: t('homepage.testimonials.customer1.title'),
      content: t('homepage.testimonials.customer1.content'),
      avatar: "/avatars/user1.jpg"
    },
    {
      id: 2,
      name: t('homepage.testimonials.customer2.name'),
      title: t('homepage.testimonials.customer2.title'),
      content: t('homepage.testimonials.customer2.content'),
      avatar: "/avatars/user2.jpg"
    },
    {
      id: 3,
      name: t('homepage.testimonials.customer3.name'),
      title: t('homepage.testimonials.customer3.title'),
      content: t('homepage.testimonials.customer3.content'),
      avatar: "/avatars/user3.jpg"
    }
  ];

  return (
    <HomeContainer>
      {/* 英雄区域 */}
      <HeroSection>
        <Row justify="center" style={{ margin: 0, width: '100%' }}>
          <ResponsiveCol xs={24} sm={22} md={20} lg={16} xl={14}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>  {/* 减小组件间距 */}
              <HeroTitle level={1}>
                {t('homepage.hero.title')}
              </HeroTitle>
              <HeroSubtitle>
                {t('homepage.hero.subtitle')}
              </HeroSubtitle>
              <HeroSlogan>
                {t('homepage.hero.slogan')}
              </HeroSlogan>

              {state.isAuthenticated ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Text style={{ fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                    {t('homepage.welcome.title', { name: state.user?.display_name || state.user?.email })}
                  </Text>
                  <ModernButton 
                    className="white"
                    size="large" 
                    icon={<MessageOutlined />}
                    onClick={() => navigate('/chat')}
                  >
                    {t('homepage.hero.startConsultation')}
                  </ModernButton>
                </Space>
              ) : (
                <Space 
                  size={isMobile ? "middle" : "large"} 
                  style={{ 
                    flexWrap: 'wrap', 
                    justifyContent: 'center',
                    width: '100%'
                  }}
                  direction={isMobile ? "vertical" : "horizontal"}
                >
                  <ModernButton 
                    className="white"
                    size="large" 
                    icon={<MessageOutlined />}
                    onClick={() => navigate('/chat')}
                    style={{ width: isMobile ? '200px' : 'auto' }}
                  >
                    {t('homepage.hero.startConsultation')}
                  </ModernButton>
                  <ModernButton 
                    className="transparent"
                    size="large" 
                    icon={<UserAddOutlined />}
                    onClick={() => navigate('/register')}
                    style={{ width: isMobile ? '200px' : 'auto' }}
                  >
                    {t('auth.register')}
                  </ModernButton>
                </Space>
              )}
            </Space>
          </ResponsiveCol>
        </Row>
      </HeroSection>

      {/* 媒体报道区域 - 暂时注释掉，以后可能会用 */}
      {/* <FeaturedSection>
        <Row justify="center">
          <Col xs={24} sm={20} md={16}>
            <Title level={3} style={{ 
              color: '#666', 
              marginBottom: '2rem',
              fontSize: '1.1rem',
              fontWeight: 400
            }}>
              多くのメディアで紹介されています
            </Title>
            <FeaturedLogos>
              <FeaturedLogo>日経新聞</FeaturedLogo>
              <FeaturedLogo>朝日新聞</FeaturedLogo>
              <FeaturedLogo>TechCrunch</FeaturedLogo>
              <FeaturedLogo>IT media</FeaturedLogo>
            </FeaturedLogos>
          </Col>
        </Row>
      </FeaturedSection> */}

      {/* 服务特色区域 */}
      <ServicesSection>
        <Row justify="center">
          <Col xs={24} sm={20} md={18} lg={16}>
            <ServicesTitleSection>
              <Title level={2} style={getTitleStyle(isMobile)}>
                {t('homepage.features.title')}
              </Title>
              <Paragraph className="description" style={{ 
                fontSize: '1.125rem', 
                color: '#4a5568', 
                lineHeight: 1.6,
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                {t('homepage.features.subtitle')}
              </Paragraph>
            </ServicesTitleSection>

            <Row gutter={isMobile ? [16, 20] : [24, 24]} justify="center">
              {features.map((feature, index) => (
                <Col key={index} xs={22} sm={12} lg={8}>
                  <ServiceCard>
                    <div style={{ marginBottom: isMobile ? '0.8rem' : '1rem' }}>
                      {feature.icon}
                    </div>
                    <Title level={3} style={{ 
                      fontSize: isMobile ? '1.1rem' : '1.5rem', 
                      fontWeight: 600,
                      color: '#333',
                      marginBottom: isMobile ? '0.5rem' : '1rem'
                    }}>
                      {feature.title}
                    </Title>
                    <Paragraph style={{ 
                      color: '#666', 
                      lineHeight: isMobile ? 1.4 : 1.6,
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      marginBottom: 0
                    }}>
                      {feature.description}
                    </Paragraph>
                  </ServiceCard>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </ServicesSection>

      {/* 三步骤说明区域 */}
      <HowItWorksSection>
        <Row justify="center">
          <Col xs={24} sm={20} md={18} lg={16}>
            <ServicesTitleSection>
              <Title level={2} style={getTitleStyle(isMobile)}>
                {t('homepage.steps.title')}
              </Title>
              <Paragraph className="description" style={{ 
                fontSize: isMobile ? '0.9rem' : '1.125rem',
                color: '#4a5568', 
                lineHeight: isMobile ? 1.4 : 1.6,
                maxWidth: '600px',
                margin: '0 auto',
                marginBottom: isMobile ? '1.5rem' : '2rem'
              }}>
                {t('homepage.steps.subtitle')}
              </Paragraph>
            </ServicesTitleSection>

            <Row gutter={isMobile ? [8, 16] : [24, 24]} justify="center">
              {[1, 2, 3].map((step) => (
                <Col key={step} xs={22} sm={8}>
                  <StepContainer>
                    <StepNumber>{step}</StepNumber>
                    <Title level={3} style={{ 
                      fontSize: isMobile ? '1.1rem' : '1.5rem',
                      fontWeight: 600,
                      color: '#333',
                      marginBottom: isMobile ? '0.3rem' : '1rem'
                    }}>
                      {t(`homepage.steps.step${step}.title`)}
                    </Title>
                    <Paragraph style={{ 
                      color: '#666',
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      lineHeight: isMobile ? 1.4 : 1.6,
                      marginBottom: 0
                    }}>
                      {t(`homepage.steps.step${step}.description`)}
                    </Paragraph>
                  </StepContainer>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </HowItWorksSection>

      {/* 统计数据 - 暂时注释掉，以后可能会用 */}
      {/* <StatsSection>
        <Row justify="center">
          <Col xs={24} sm={20} md={18} lg={16}>
            <Row gutter={[48, 24]} justify="center">
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('homepage.stats.precedents')}
                  value={30000}
                  suffix="+"
                  valueStyle={{ color: '#667eea', fontSize: '2rem', fontWeight: 700 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('homepage.stats.personalUsers')}
                  value={80000}
                  suffix="+"
                  valueStyle={{ color: '#667eea', fontSize: '2rem', fontWeight: 700 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('homepage.stats.responseTime')}
                  value={30}
                  suffix={t('homepage.stats.seconds')}
                  valueStyle={{ color: '#667eea', fontSize: '2rem', fontWeight: 700 }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('homepage.stats.resolutionRate')}
                  value={95}
                  suffix="%"
                  valueStyle={{ color: '#667eea', fontSize: '2rem', fontWeight: 700 }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </StatsSection> */}

      {/* 用户证言区域 */}
      <TestimonialsSection>
        <Row justify="center">
          <Col xs={24} sm={20} md={18} lg={16}>
            <TestimonialSection>
              <Title level={2} style={getTitleStyle(isMobile)}>
                {t('homepage.testimonials.title')}
              </Title>
            </TestimonialSection>

            <Row gutter={isMobile ? [16, 20] : [24, 24]} justify="center">
              {testimonials.map((testimonial, index) => (
                <Col key={testimonial.id} xs={22} sm={12} lg={8}>
                  <TestimonialCard>
                    <Paragraph style={{ 
                      fontStyle: 'italic',
                      marginBottom: isMobile ? '0.5rem' : '1rem',  // 减小段落底部间距
                      color: '#555',
                      fontSize: isMobile ? '0.9rem' : '1rem',  // 减小字体大小
                      lineHeight: isMobile ? 1.4 : 1.6  // 减小行高
                    }}>
                      "{testimonial.content}"
                    </Paragraph>
                    <Text style={{ 
                      fontWeight: 'bold',
                      color: '#333',
                      fontSize: isMobile ? '0.85rem' : '1rem'  // 减小字体大小
                    }}>
                      - {testimonial.name} ({testimonial.title})
                    </Text>
                  </TestimonialCard>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </TestimonialsSection>

      {/* 免责声明区域 */}
      <DisclaimerSection>
        <Row justify="center">
          <Col xs={24} sm={20} md={18} lg={16}>
            <DisclaimerCard>
              <Title level={3} style={{ 
                color: '#333', 
                marginBottom: '1.5rem',
                fontSize: isMobile ? '1.3rem' : '1.5rem',
                textAlign: 'center'
              }}>
                {i18n.language === 'ja' ? '重要なお知らせ' : '重要通知'}
              </Title>
              <Paragraph style={{ 
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: 1.6,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'left'
              }}>
                {i18n.language === 'ja' 
                  ? '本サービスは、法律に関する情報提供や類似事例の紹介を目的としたAIによる補助ツールです。以下の点をご確認いただき、同意の上でご利用ください。'
                  : '本服务是一个由AI辅助提供的法律信息工具，旨在提供法律相关的一般性信息与类似案例的参考。使用本服务前，请确认并同意以下内容：'
                }
              </Paragraph>
              <div style={{ 
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: 1.6,
                color: '#555',
                marginBottom: '1.5rem',
                textAlign: 'left',
                backgroundColor: '#f0f8ff',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid #e6f3ff'
              }}>
                <ul style={{
                  margin: '0',
                  padding: '0',
                  listStyle: 'none'
                }}>
                  {(i18n.language === 'ja' ? [
                    '本サービスは弁護士による法律相談・法律業務の提供ではありません。',
                    'AIが提供する回答は、一般的な情報や過去の事例に基づくものであり、特定の事件・事案に対する正式な法的助言ではありません。',
                    '緊急性の高い問題、複雑な法律問題、個別の事情に応じた対応が必要な場合は、必ず弁護士などの専門家にご相談ください。',
                    '本サービスの利用によって生じた損害等について、当方は一切の責任を負いかねます。'
                  ] : [
                    '本服务并非由律师提供的正式法律咨询或法律服务。',
                    'AI提供的答复仅基于公开资料和过往案例，不构成对您具体案件的法律建议。',
                    '对于紧急、复杂、涉及具体个人情况的法律问题，请务必咨询专业律师。',
                    '对因使用本服务所引起的任何后果或损失，平台方概不承担责任。'
                  ]).map((item, index) => (
                    <li key={index} style={{
                      marginBottom: '1rem',
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      lineHeight: 1.6,
                      color: '#333',
                      position: 'relative',
                      paddingLeft: '1.5rem'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: '0',
                        top: '0.2rem',
                        color: '#667eea',
                        fontWeight: 'bold',
                        fontSize: '1.2rem'
                      }}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <Paragraph style={{ 
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: 1.6,
                color: '#333',
                fontWeight: 'bold',
                textAlign: 'left',
                marginBottom: 0
              }}>
                {i18n.language === 'ja' 
                  ? '上記をご理解いただいた上で、AIとのチャットを開始してください。'
                  : '如您已充分理解并同意以上内容，请开始与AI进行对话。'
                }
              </Paragraph>
            </DisclaimerCard>
          </Col>
        </Row>
      </DisclaimerSection>

      {/* CTA区域 */}
      <CTASection>
        <Row justify="center">
          <Col xs={24} sm={20} md={16} lg={12}>
            <Space direction="vertical" size={isMobile ? "middle" : "large"} style={{ width: '100%' }}>  {/* 减小组件间距 */}
              <CTATitle level={2} style={getTitleStyle(isMobile, true)}>
                {t('homepage.cta.title')}
              </CTATitle>
              <Paragraph style={{ 
                fontSize: isMobile ? '1rem' : '1.2rem',
                color: 'rgba(255, 255, 255, 0.9)', 
                lineHeight: isMobile ? 1.4 : 1.6,
                maxWidth: '600px',
                margin: isMobile ? '0 auto 1rem' : '0 auto 2rem',  // 统一使用margin来控制间距
                whiteSpace: 'pre-line'
              }}>
                {t('homepage.cta.subtitle')}
              </Paragraph>
              <ModernButton 
                className="white"
                size="large" 
                icon={<MessageOutlined />}
                onClick={() => navigate('/chat')}
                style={{ width: isMobile ? '250px' : 'auto' }}
              >
                {t('homepage.cta.freeRegister')}
              </ModernButton>
            </Space>
          </Col>
        </Row>
      </CTASection>
      </HomeContainer>
    );
  };

export default HomePage; 